<template>
    <nuxt-link v-if="url"
        :to="url"
        :aria-label="label !== '' ? label : emptyLabel"
        :title="label !== '' ? label : emptyLabel"
        class="group relative">
        <div class="overflow-hidden rounded">
            <nuxt-picture v-if="!ad.use_as_slideshow && ad.background_image?.filename !== null"
                :src="ad.background_image?.filename"
                :alt="ad.background_image.alt ?? ad.title"
                provider="storyblok"
                :preload="{ fetchPriority: 'low' }"
                width="372"
                height="372"
                sizes="xs:50vw sm:316px md:245px lg:244px xl:308px 2xl:372px"
                :img-attrs="{
                    class: 'object-cover h-full w-full filter:blur-[5px]'
                }"
                class="aspect-square" />
        </div>

        <!-- Carousel -->
        <template v-if="ad.images && ad.images.length > 0 && ad.use_as_slideshow">
            <v-auto-scroller :items="ad.images">
                <template #item="{ filename, alt, title }">
                    <nuxt-picture :src="filename"
                        :alt="alt ?? title"
                        provider="storyblok"
                        loading="lazy"
                        :preload="{ fetchPriority: 'low' }"
                        width="372"
                        height="372"
                        sizes="xs:50vw sm:316px md:245px lg:244px xl:308px 2xl:372px"
                        :img-attrs="{
                            class: 'object-cover h-full w-full filter:blur-[5px] rounded'
                        }"
                        class="aspect-square rounded" />
                </template>
            </v-auto-scroller>
        </template>

        <div class="p-2">
            <v-subtitle class="truncate font-bold leading-4 tracking-xl group-hover:underline md:text-base md:leading-5"
                size="sm">
                {{ ad.title }}
            </v-subtitle>

            <p class="font-sans text-sm text-gray-500 md:text-base">
                {{ ad.content }}
            </p>
        </div>
    </nuxt-link>

    <div v-else
        class="group relative">
        <div class="overflow-hidden rounded">
            <nuxt-picture v-if="!ad.use_as_slideshow && ad.background_image?.filename !== null"
                :src="ad.background_image?.filename"
                :alt="ad.background_image.alt ?? ad.title"
                provider="storyblok"
                loading="lazy"
                :preload="{ fetchPriority: 'low' }"
                width="372"
                height="372"
                sizes="xs:50vw sm:316px md:245px lg:244px xl:308px 2xl:372px"
                :img-attrs="{
                    class: 'object-cover h-full w-full filter:blur-[5px]'
                }"
                class="aspect-square" />
        </div>

        <div v-if="ad.title && ad.content"
            class="p-2">
            <v-subtitle class="font-bold leading-4 tracking-xl group-hover:underline md:text-base md:leading-5"
                size="sm">
                {{ ad.title }}
            </v-subtitle>

            <p class="line-clamp-3 font-sans text-sm text-gray-500 md:text-base">
                {{ ad.content }}
            </p>
        </div>

        <!-- Carousel -->
        <template v-if="ad.images && ad.images.length > 0 && ad.use_as_slideshow">
            <v-auto-scroller :items="ad.images">
                <template #item="{ filename, alt, title }">
                    <nuxt-picture :src="filename"
                        :alt="alt ?? title"
                        provider="storyblok"
                        loading="lazy"
                        :preload="{ fetchPriority: 'low' }"
                        width="372"
                        height="372"
                        sizes="xs:50vw sm:316px md:245px lg:244px xl:308px 2xl:372px"
                        :img-attrs="{
                            class: 'object-cover h-full w-full filter:blur-[5px] rounded'
                        }"
                        class="aspect-square rounded" />
                </template>
            </v-auto-scroller>
        </template>
    </div>
</template>

<script lang="ts" setup>
import type { ListingAdComponent } from '~/types';

const properties = defineProps<{
    ad: ListingAdComponent
}>();

const { t } = useI18n();

const { format: formatSbUrl } = useSbUrl();
const { format: stripUrl } = useStripUrl();

const label = computed(() => {
    const title = properties.ad.title?.trim();
    const content = properties.ad.content?.trim();
    return title && content ? `${title} ${content}` : '';
});

// eslint-disable-next-line max-len
const emptyLabel = computed(() => t('accessibility.navigate-to', { page: `${stripUrl(properties.ad.link.cached_url) ?? ''}` }));

const url = computed(() => (properties.ad.link?.cached_url ? formatSbUrl(properties.ad.link.cached_url) : undefined));
</script>
